/**
 * Composant React pour la bannière de consentement GDPR
 * Boss SEO Plugin
 */

import React, { useState, useEffect } from 'react';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';

const GDPRConsentBanner = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [consents, setConsents] = useState({
        analytics: false,
        marketing: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [settings, setSettings] = useState({
        banner_title: __('Nous respectons votre vie privée', 'boss-seo'),
        banner_message: __('Nous utilisons des cookies pour améliorer votre expérience sur notre site. Certains sont nécessaires au fonctionnement du site, d\'autres nous aident à comprendre comment vous l\'utilisez pour l\'améliorer.', 'boss-seo')
    });

    useEffect(() => {
        // Vérifier si le consentement a déjà été donné
        if (!hasUserConsent()) {
            // Charger les paramètres GDPR
            loadGDPRSettings();
            // Afficher la bannière après un délai
            setTimeout(() => setIsVisible(true), 500);
        }
    }, []);

    const loadGDPRSettings = async () => {
        try {
            const response = await apiFetch({
                path: '/boss-seo/v1/gdpr/settings'
            });
            if (response.success) {
                setSettings(response.data);
            }
        } catch (error) {
            
        }
    };

    const hasUserConsent = () => {
        try {
            const cookieValue = getCookie('boss_seo_consent');
            if (cookieValue) {
                const consentData = JSON.parse(decodeURIComponent(cookieValue));
                if (consentData && consentData.timestamp) {
                    // Vérifier si le consentement n'est pas expiré (12 mois)
                    const now = Math.floor(Date.now() / 1000);
                    return (now - consentData.timestamp) < (365 * 24 * 60 * 60);
                }
            }
        } catch (error) {
            
        }
        return false;
    };

    const getCookie = (name) => {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    };

    const saveConsent = async (consentType) => {
        setIsLoading(true);
        
        let consentData = {};
        if (consentType === 'all') {
            consentData = { analytics: true, marketing: true };
        } else if (consentType === 'none') {
            consentData = { analytics: false, marketing: false };
        } else {
            consentData = consents;
        }

        try {
            const response = await apiFetch({
                path: '/boss-seo/v1/gdpr/consent',
                method: 'POST',
                data: {
                    analytics: consentData.analytics,
                    marketing: consentData.marketing
                }
            });

            if (response && response.success) {
                // Déclencher l'événement de consentement sauvegardé
                window.dispatchEvent(new CustomEvent('boss_seo_consent_saved', {
                    detail: { consents: consentData }
                }));
                
                // Masquer la bannière
                setIsVisible(false);
                
                // Afficher une notification de succès
                showNotification((response.message || __('Consentement enregistré avec succès', 'boss-seo')), 'success');
            } else {
                const errorMessage = (response && response.message) || __('Erreur lors de la sauvegarde', 'boss-seo');
                showNotification(errorMessage, 'error');
            }
        } catch (error) {
            
            showNotification(__('Erreur de connexion. Veuillez réessayer.', 'boss-seo'), 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const showNotification = (message, type) => {
        // Créer une notification temporaire
        const notification = document.createElement('div');
        notification.className = `boss-seo-notification boss-seo-notification-${type}`;
        notification.innerHTML = `
            <span class="notification-message">${message}</span>
            <button class="notification-close" aria-label="${__('Fermer', 'boss-seo')}">&times;</button>
        `;
        
        // Styles inline pour éviter les conflits
        Object.assign(notification.style, {
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: type === 'success' ? '#00a32a' : '#d63638',
            color: '#ffffff',
            padding: '12px 16px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: '1000000',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: '14px',
            maxWidth: '300px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        });
        
        document.body.appendChild(notification);
        
        // Fermeture automatique
        setTimeout(() => {
            if (notification.parentNode) {
                notification.remove();
            }
        }, 5000);
        
        // Fermeture manuelle
        notification.querySelector('.notification-close').addEventListener('click', () => {
            notification.remove();
        });
    };

    const handleConsentChange = (type, value) => {
        setConsents(prev => ({
            ...prev,
            [type]: value
        }));
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setIsVisible(false);
        }
    };

    useEffect(() => {
        if (isVisible) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <div 
            className={`boss-gdpr-banner ${isVisible ? 'show' : ''}`}
            role="dialog"
            aria-labelledby="consent-title"
            aria-describedby="consent-description"
        >
            <div className="banner-content">
                <div className="banner-text">
                    <h3 id="consent-title">{settings.banner_title}</h3>
                    <p id="consent-description" dangerouslySetInnerHTML={{ __html: settings.banner_message }} />
                </div>
                <div className="banner-actions">
                    <button
                        type="button"
                        className={`gdpr-btn btn-primary ${isLoading ? 'is-loading' : ''}`}
                        onClick={() => saveConsent('all')}
                        disabled={isLoading}
                    >
                        {__('Tout accepter', 'boss-seo')}
                    </button>
                    <button
                        type="button"
                        className={`gdpr-btn btn-secondary ${isLoading ? 'is-loading' : ''}`}
                        onClick={() => saveConsent('none')}
                        disabled={isLoading}
                    >
                        {__('Tout refuser', 'boss-seo')}
                    </button>
                    <button
                        type="button"
                        className="gdpr-btn btn-outline"
                        onClick={() => setShowDetails(!showDetails)}
                        disabled={isLoading}
                    >
                        {showDetails ? __('Masquer les détails', 'boss-seo') : __('Personnaliser', 'boss-seo')}
                    </button>
                </div>
            </div>
            
            {showDetails && (
                <div className="gdpr-customize-panel">
                    <h4>{__('Personnaliser vos préférences', 'boss-seo')}</h4>
                    <div className="consent-options">
                        <div className="consent-option">
                        <label>
                            <input 
                                type="checkbox" 
                                checked={true} 
                                disabled 
                                readOnly
                            />
                            <div>
                                <strong>{__('Cookies nécessaires', 'boss-seo')}</strong>
                                <span className="required">({__('Obligatoire', 'boss-seo')})</span>
                                <p>{__('Ces cookies sont nécessaires au fonctionnement du site.', 'boss-seo')}</p>
                            </div>
                        </label>
                        </div>
                        
                        <div className="consent-option">
                        <label>
                            <input 
                                type="checkbox" 
                                checked={consents.analytics}
                                onChange={(e) => handleConsentChange('analytics', e.target.checked)}
                            />
                            <div>
                                <strong>{__('Cookies analytiques', 'boss-seo')}</strong>
                                <p>{__('Ces cookies nous aident à améliorer notre site en collectant des informations anonymes sur votre utilisation.', 'boss-seo')}</p>
                            </div>
                        </label>
                        </div>
                        
                        <div className="consent-option">
                        <label>
                            <input 
                                type="checkbox" 
                                checked={consents.marketing}
                                onChange={(e) => handleConsentChange('marketing', e.target.checked)}
                            />
                            <div>
                                <strong>{__('Cookies marketing', 'boss-seo')}</strong>
                                <p>{__('Ces cookies sont utilisés pour vous proposer des publicités pertinentes.', 'boss-seo')}</p>
                            </div>
                        </label>
                        </div>
                    </div>
                    
                    <div className="banner-actions">
                        <button
                            type="button"
                            className={`gdpr-btn btn-primary ${isLoading ? 'is-loading' : ''}`}
                            onClick={() => saveConsent('custom')}
                            disabled={isLoading}
                        >
                            {__('Enregistrer mes préférences', 'boss-seo')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GDPRConsentBanner;
